<template>
  <div class="product-carbon-footprint">
    <div class="pcf-list-wrap">
      <div
        v-for="(block, bi) in certificateBlocks"
        :key="`block-${bi}`"
        :ref="`pcfBlock${bi}`"
        class="pcf-block-card"
        :class="{ 'is-collapsed-row': block.collapsed }"
      >
        <div class="pcf-block-header">
          <div class="pcf-block-header-left" @click="toggleBlockCollapse(bi)">
            <span class="pcf-block-title-tag">产品碳足迹{{ bi + 1 }}</span>
            <span class="pcf-collapse-box">
              <i :class="block.collapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'" />
            </span>
          </div>
          <div
            v-if="canChangeStructure"
            class="pcf-block-header-right"
            :class="{ 'is-actions-hidden': block.collapsed }"
          >
            <button
              v-if="bi === certificateBlocks.length - 1 && certificateBlocks.length < maxCertificateBlocks"
              type="button"
              class="pcf-add-certificate-btn"
              @click.stop="addCertificateBlock"
            >
              + 新增产品碳足迹
            </button>
            <eui-btn v-if="certificateBlocks.length > 1" type="text" color="#E50113" @click.stop="removeCertificateBlock(bi)">删除</eui-btn>
          </div>
        </div>

      <div class="pcf-block-body" :class="{ 'is-collapsed': block.collapsed }">
        <div class="pcf-block-body-inner">
      <el-form
        :ref="`blockForm${bi}`"
        :model="block"
        :rules="rulesForBlock(bi)"
        size="small"
        label-width="160px"
        label-position="right"
        label-suffix=":"
        :validate-on-rule-change="false"
        class="pcf-block-form"
      >
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="出具机构" prop="institutionName">
              <el-input v-model="block.institutionName" :disabled="readonly" maxlength="255" placeholder="请输入出具机构" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="发布日期" prop="publishDate">
              <el-date-picker v-model="block.publishDate" :disabled="readonly" style="width: 100%" type="date" value-format="yyyy-MM-dd" placeholder="请选择发布日期" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item prop="systemBoundary" class="label-wrap-form-item">
              <template #label>
                <form-label-with-tip label="系统边界" :tip="termTips.systemBoundary" />
              </template>
              <el-select v-model="block.systemBoundary" :disabled="readonly" placeholder="请选择系统边界" style="width: 100%">
                <el-option label="摇篮到大门（Cradle-to-Gate）" :value="1" />
                <el-option label="大门到大门（Gate-to-Gate）" :value="2" />
                <el-option label="摇篮到坟墓（Cradle-to-Grave）" :value="3" />
                <el-option label="摇篮到摇篮（Cradle-to-Cradle）" :value="4" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item prop="timeBoundary" class="label-wrap-form-item">
              <template #label>
                <form-label-with-tip label="时间边界" :tip="termTips.timeBoundary" />
              </template>
              <el-date-picker
                v-model="block.timeBoundary"
                :disabled="readonly"
                style="width: 100%"
                type="daterange"
                value-format="yyyy-MM-dd"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 查看详情 / 列表编辑弹窗：隐藏产品 Tab 切换栏 -->
        <div v-if="showProductTabs" class="product-tabs-panel">
          <div class="product-tabs-inner">
            <button
              v-if="canChangeStructure"
              type="button"
              class="product-tab-add"
              :disabled="block.products.length >= maxProductsPerCertificate"
              @click="addProduct(bi)"
            >
              + 新增产品
            </button>
            <div
              v-for="(prod, pi) in block.products"
              :key="`tab-${bi}-${pi}`"
              :class="['product-tab-item', { active: block.activeProductIndex === pi }]"
              @click="selectProduct(bi, pi)"
            >
              <el-tooltip :content="productTabLabel(prod, pi)" placement="top" effect="dark" :open-delay="300">
                <span class="product-tab-text">{{ productTabLabel(prod, pi) }}</span>
              </el-tooltip>
              <i v-if="canChangeStructure && block.products.length > 1" class="el-icon-close product-tab-close" @click.stop="removeProduct(bi, pi)" />
            </div>
          </div>
        </div>
        <span v-if="showProductTabs && block.products.length >= maxProductsPerCertificate" class="limit-tip product-tabs-limit">
          单份证书下最多录入 {{ maxProductsPerCertificate }} 个产品
        </span>

        <div v-for="(prod, pi) in block.products" v-show="block.activeProductIndex === pi" :key="`panel-${bi}-${pi}`" class="product-panel">
          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item :prop="`products.${pi}.productName`" :label="`产品${pi + 1}名称`">
                <el-input v-model="prod.productName" :disabled="readonly" maxlength="255" placeholder="请输入产品名称" />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :prop="`products.${pi}.specifications`" label="规格型号">
                <el-input v-model="prod.specifications" :disabled="readonly" maxlength="255" placeholder="请输入规格型号（选填）" />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :prop="`products.${pi}.functionalUnit`" class="label-wrap-form-item">
                <template #label>
                  <form-label-with-tip label="功能单位" :tip="termTips.functionalUnit" />
                </template>
                <el-input v-model="prod.functionalUnit" :disabled="readonly" maxlength="100" placeholder="请输入功能单位，吨/米/个/台等" />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="24">
              <el-form-item :prop="`products.${pi}.certificateMaterialCategory`" label="认证物资类别" class="material-category-form-item">
                <certificateMaterialCategory
                  :category="prod.certificateMaterialCategory"
                  :suppress-remote-requests="readonly"
                  @change="(value) => onCategoryChange(bi, pi, value)"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="24">
              <el-form-item class="label-wrap-form-item carbon-unit-main-form-item" :prop="`products.${pi}.carbonUnitValue`">
                <template #label>
                  <form-label-with-tip label="每功能单位碳足迹数值" :tip="termTips.carbonUnitValue" />
                </template>
                <div class="carbon-footprint-value-row">
                  <el-input
                    v-model="prod.carbonUnitValue"
                    :disabled="readonly"
                    class="carbon-value-input"
                    maxlength="100"
                    placeholder="请输入每功能单位碳足迹数值"
                    @input="(val) => onCarbonUnitValueInput(val, bi, pi)"
                    @blur="() => validateCarbonFootprintPairFields(bi, pi)"
                  />
                  <el-select
                    v-model="prod.carbonUnit"
                    :disabled="readonly"
                    class="carbon-unit-select"
                    placeholder="请选择单位"
                    clearable
                    @change="() => validateCarbonFootprintPairFields(bi, pi)"
                  >
                    <el-option
                      v-for="opt in carbonUnitOptions"
                      :key="opt.value"
                      :label="opt.label"
                      :value="opt.value"
                    />
                  </el-select>
                </div>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="24">
              <el-form-item
                label-width="0"
                class="lifecycle-block-form-item"
                :prop="`products.${pi}.lifeCarbonList`"
              >
                <lifecycle-emissions
                  :linked-unit-mode="true"
                  :form-label-width="150"
                  :emissions="prod"
                  :readonly="readonly"
                  :show-empty-field-errors="!!lifeCarbonFieldErrorKeys[`${bi}_${pi}`]"
                  @input="(data) => onLifecycleInput(data, bi, pi)"
                  @lifecycleCarbonUnitChange="(v) => onLifecycleCarbonUnitChange(v, bi, pi)"
                />
              </el-form-item>
            </el-col>
          </el-row>
        </div>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="碳足迹报告" prop="reportAttachmentList" class="report-cert-upload-form-item">
              <eui-upload
                v-if="!readonly"
                :headers="headers"
                :file-list="block.reportAttachmentList"
                :action="fileUrl"
                :auto-upload="autoUpload"
                :show-file-list="true"
                :limit="1"
                accept=".pdf"
                text="上传"
                :limit-file-size="30"
                tip="最多上传1个文件，格式为PDF，大小不超过30M"
                @handleSuccess="(res) => onReportUpload(res, bi)"
              />
              <div
                v-else-if="block.reportAttachmentList && block.reportAttachmentList.length"
                class="readonly-file-name is-link"
                @click="downloadAttachment(block.reportAttachmentList[0])"
              >
                {{ block.reportAttachmentList[0].name }}
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="报告编号" prop="reportCode">
              <el-input v-model="block.reportCode" :disabled="readonly" maxlength="100" placeholder="请输入报告编号" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="碳足迹证书" prop="certificateAttachmentList" class="report-cert-upload-form-item">
              <eui-upload
                v-if="!readonly"
                :headers="headers"
                :file-list="block.certificateAttachmentList"
                :action="fileUrl"
                :auto-upload="autoUpload"
                :show-file-list="true"
                :limit="1"
                accept=".pdf"
                text="上传"
                :limit-file-size="30"
                tip="最多上传1个文件，格式为PDF，大小不超过30M"
                @handleSuccess="(res) => onCertUpload(res, bi)"
              />
              <div
                v-else-if="block.certificateAttachmentList && block.certificateAttachmentList.length"
                class="readonly-file-name is-link"
                @click="downloadAttachment(block.certificateAttachmentList[0])"
              >
                {{ block.certificateAttachmentList[0].name }}
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="证书编号" prop="certificateNumber">
              <el-input v-model="block.certificateNumber" :disabled="readonly" maxlength="100" placeholder="请输入证书编号" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
        </div>
      </div>
      </div>
    </div>

    <div v-if="canChangeStructure && certificateBlocks.length < maxCertificateBlocks" class="pcf-footer-add">
      <button type="button" class="pcf-footer-add-btn" @click="addCertificateBlock">+ 新增产品碳足迹</button>
    </div>
  </div>
</template>

<script>
import api from '@/modules/projectA/api/order';
import { epecGrayGroup } from '@/modules/projectA/api/config';
import { epecToken } from '@/modules/projectA/utils/globalData';
import certificateMaterialCategory from './certificateMaterialCategory.vue';
import formLabelWithTip from './formLabelWithTip.vue';
import lifecycleEmissions from './lifecycleEmissions.vue';
import { normalizeLifecycleCarbonUnit, CARBON_UNIT_OPTIONS } from '../utils/carbonUnits';
import { isMaterialCategoryLeafNode } from '../utils/materialCategoryLevelIds';
import {
  CARBON_FOOTPRINT_TERM_TIPS,
  DELETE_CONFIRM_MESSAGE,
  REPORT_OR_CERT_UPLOAD_TIP,
  REPORT_OR_CERT_UPLOAD_ERROR,
} from '../utils/carbonFootprintTermTips';

const defaultLifeCarbonList = () => [
  { stageType: 1, carbonEmission: '', proportion: '' },
  { stageType: 2, carbonEmission: '', proportion: '' },
  { stageType: 3, carbonEmission: '', proportion: '' },
  { stageType: 4, carbonEmission: '', proportion: '' },
  { stageType: 5, carbonEmission: '', proportion: '' },
  { stageType: 6, carbonEmission: '', proportion: '' },
];

const LIFECYCLE_STAGE_NAMES = {
  1: '原材料获取阶段（上游生产）',
  2: '原材料获取阶段（运输）',
  3: '生产阶段',
  4: '运输（交付）阶段',
  5: '使用阶段',
  6: '生命末期阶段',
};

function validateCertificateMaterialCategory(_rule, value, callback) {
  if (!value || !Array.isArray(value) || value.length === 0) {
    callback(new Error('请选择认证物资类别'));
    return;
  }
  const first = value[0];
  const hasId = first.categoryId !== undefined && first.categoryId !== null && String(first.categoryId).trim() !== '';
  const hasName = first.categoryName && String(first.categoryName).trim() !== '';
  if (!hasId && !hasName) {
    callback(new Error('请选择认证物资类别'));
    return;
  }
  callback();
}

function createEmptyProduct() {
  return {
    certificateMaterialCategory: [],
    productName: '',
    specifications: '',
    functionalUnit: '',
    carbonUnitValue: '',
    /** 每功能单位碳足迹单位：1 吨 / 2 千克 / 3 克（与 lifecycleCarbonUnit 保持一致） */
    carbonUnit: '',
    lifeCarbonList: defaultLifeCarbonList(),
    standardName: '',
  };
}

function createEmptyCertificateBlock() {
  return {
    institutionName: '',
    publishDate: '',
    systemBoundary: '',
    timeBoundary: [],
    certificateNumber: '',
    reportCode: '',
    certificateAttachmentList: [],
    reportAttachmentList: [],
    products: [createEmptyProduct()],
    activeProductIndex: 0,
    collapsed: false,
  };
}

function productFieldsFromApiRow(r) {
  const row = createEmptyProduct();
  if (!r || typeof r !== 'object') {
    return row;
  }
  row.productName = r.productName || '';
  row.specifications = r.specifications || '';
  row.functionalUnit = r.functionalUnit || '';
  const valueFromApi = r.carbonUnitValue != null ? String(r.carbonUnitValue).trim() : '';
  const rawCarbonUnit = r.carbonUnit != null ? String(r.carbonUnit).trim() : '';
  const footprintUnitNum = Number(rawCarbonUnit);
  const isFootprintUnitCode = footprintUnitNum === 1 || footprintUnitNum === 2 || footprintUnitNum === 3;

  if (valueFromApi) {
    row.carbonUnitValue = valueFromApi;
  } else if (rawCarbonUnit && !isFootprintUnitCode) {
    // 兼容旧数据：数值曾写在 carbonUnit
    row.carbonUnitValue = rawCarbonUnit;
  }
  if (isFootprintUnitCode) {
    row.carbonUnit = footprintUnitNum;
  }
  const lifecycleFromApi = normalizeLifecycleCarbonUnit(r.lifecycleCarbonUnit);
  if (row.carbonUnit === '' && lifecycleFromApi !== '') {
    row.carbonUnit = lifecycleFromApi;
  }
  row.standardName = r.standardName || '';
  const catName = r.categoryName;
  const catId = r.categoryId;
  const lv1 = r.firstCategoryId;
  const lv2 = r.secondCategoryId;
  const lv3 = r.thirdCategoryId;
  const lv4 = r.fourthCategoryId;
  const fromArr = r.certificateMaterialCategory;
  if (fromArr && Array.isArray(fromArr) && fromArr.length) {
    row.certificateMaterialCategory = JSON.parse(JSON.stringify(fromArr));
  } else if (catName || catId !== undefined) {
    const catItem = { categoryName: catName, categoryId: catId };
    const hasLevelIds = [lv1, lv2, lv3, lv4].some((v) => v !== undefined && v !== null && String(v).trim() !== '');
    if (hasLevelIds) {
      catItem.firstCategoryId = lv1 != null ? lv1 : null;
      catItem.secondCategoryId = lv2 != null ? lv2 : null;
      catItem.thirdCategoryId = lv3 != null ? lv3 : null;
      catItem.fourthCategoryId = lv4 != null ? lv4 : null;
    }
    if (r.mdmCategoryCode != null && String(r.mdmCategoryCode).trim() !== '') {
      catItem.mdmCategoryCode = r.mdmCategoryCode;
      catItem.isLeaf = 1;
    }
    row.certificateMaterialCategory = [catItem];
  }
  row.lifeCarbonList = r.lifeCarbonList ? JSON.parse(JSON.stringify(r.lifeCarbonList)) : defaultLifeCarbonList();
  return row;
}

export default {
  name: 'ProductCarbonFootprint',
  components: {
    certificateMaterialCategory,
    formLabelWithTip,
    lifecycleEmissions,
  },
  props: {
    certificate: {
      type: Array,
      default: () => [],
    },
    report: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    /** 列表编辑：禁止新增/删除证书块与产品，仅可改当前数据 */
    structureLocked: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    canChangeStructure() {
      return !this.readonly && !this.structureLocked;
    },
    /** 列表编辑、查看详情不展示产品 Tab 栏 */
    showProductTabs() {
      return !this.readonly && !this.structureLocked;
    },
  },
  data() {
    /** 提交时由 form.validate() 统一触发；不在 change 时打断用户（尤其新增产品后） */
    const sharedRules = {
      institutionName: [{ required: true, message: '请输入出具机构', trigger: 'blur' }],
      publishDate: [{ required: true, message: '请选择发布日期', trigger: 'blur' }],
      systemBoundary: [{ required: true, message: '请选择系统边界', trigger: 'blur' }],
    };
    const productRulesTemplate = {
      certificateMaterialCategory: [
        {
          required: true,
          validator: validateCertificateMaterialCategory,
          trigger: ['blur', 'change'],
        },
      ],
      productName: [{ required: true, message: '请输入产品名称', trigger: 'blur' }],
      functionalUnit: [{ required: true, message: '请输入功能单位', trigger: 'blur' }],
    };
    return {
      maxCertificateBlocks: 10,
      maxProductsPerCertificate: 30,
      headers: { ...epecToken },
      fileUrl: api.fileUploadForGreenPdfAndPic,
      autoUpload: true,
      certificateBlocks: [createEmptyCertificateBlock()],
      sharedRules,
      productRulesTemplate,
      termTips: CARBON_FOOTPRINT_TERM_TIPS,
      reportOrCertUploadTip: REPORT_OR_CERT_UPLOAD_TIP,
      reportOrCertUploadError: REPORT_OR_CERT_UPLOAD_ERROR,
      carbonUnitOptions: CARBON_UNIT_OPTIONS,
      /** 生命周期各阶段碳排放校验失败时，对应产品下未填输入框标红 */
      lifeCarbonFieldErrorKeys: {},
    };
  },
  watch: {
    /** 仅引用变化时回灌（如草稿刷新），避免 deep 监听误触导致覆盖正在编辑的多产品 */
    report() {
      this.hydrateFromProps();
    },
  },
  created() {
    this.hydrateFromProps();
    this.syncUploadHeaders();
  },
  methods: {
    /** 与 request.js / carbonfootprintReport 上传头一致，避免 headers 为空导致 401 */
    syncUploadHeaders() {
      const token = this.$cookies.get('epecToken');
      if (!token) {
        return;
      }
      const companyId = this.$store.state.USERINFO?.companyId;
      const headers = {
        'epec-sso-token': token,
        'epec-from': 'CORP_WEB_SITE',
        'epec-locale': 'ZH',
      };
      if (companyId) {
        headers['epec-company-id'] = companyId;
      }
      if (process.env.NODE_ENV !== 'production') {
        headers['epec-gray-group'] = epecGrayGroup;
      }
      this.headers = headers;
    },
    rulesForBlock(blockIndex) {
      const rules = { ...this.sharedRules };
      const block = this.certificateBlocks[blockIndex];
      if (!block || !block.products) {
        return rules;
      }
      block.products.forEach((_, pi) => {
        Object.keys(this.productRulesTemplate).forEach((key) => {
          rules[`products.${pi}.${key}`] = this.productRulesTemplate[key];
        });
        rules[`products.${pi}.carbonUnitValue`] = this.carbonFootprintPairRules(blockIndex, pi);
        rules[`products.${pi}.lifeCarbonList`] = this.lifeCarbonListRules(blockIndex, pi);
      });
      const uploadRules = this.reportOrCertUploadRules(blockIndex);
      rules.reportAttachmentList = uploadRules;
      rules.certificateAttachmentList = uploadRules;
      return rules;
    },
    reportOrCertUploadRules(blockIndex) {
      return [
        {
          validator: (rule, value, callback) => {
            if (this.readonly) {
              callback();
              return;
            }
            const block = this.certificateBlocks[blockIndex];
            if (!block) {
              callback();
              return;
            }
            const { certFile, reportFile } = this.getCertReportFiles(block);
            if (!this.hasUploadedAttachment(certFile) && !this.hasUploadedAttachment(reportFile)) {
              callback(new Error(this.reportOrCertUploadError));
              return;
            }
            callback();
          },
          trigger: 'change',
        },
      ];
    },
    productTabLabel(prod, pi) {
      const name = prod && prod.productName != null ? String(prod.productName).trim() : '';
      return name || `产品${pi + 1}`;
    },
    selectProduct(bi, pi) {
      const block = this.certificateBlocks[bi];
      if (!block) {
        return;
      }
      this.$set(block, 'activeProductIndex', pi);
    },
    toggleBlockCollapse(bi) {
      const block = this.certificateBlocks[bi];
      if (!block) {
        return;
      }
      this.$set(block, 'collapsed', !block.collapsed);
    },
    ensureBlockUiState(block) {
      if (block.activeProductIndex == null || block.activeProductIndex < 0) {
        this.$set(block, 'activeProductIndex', 0);
      }
      if (block.products && block.activeProductIndex >= block.products.length) {
        this.$set(block, 'activeProductIndex', Math.max(0, block.products.length - 1));
      }
      if (block.collapsed == null) {
        this.$set(block, 'collapsed', false);
      }
    },
    hydrateFromProps() {
      const repList = this.report && this.report.length ? this.report : [];
      const certList = this.certificate && this.certificate.length ? this.certificate : [];
      if (repList.length === 0 && certList.length === 0) {
        this.certificateBlocks = [createEmptyCertificateBlock()];
        this.$nextTick(() => {
          this.clearBlockFormValidation(0);
        });
        return;
      }
      if (repList.length > 0) {
        this.certificateBlocks = this.isFlatSiteGroupedReportList(repList)
          ? this.flatCarbonReportListToBlocks(repList)
          : repList.map((row) => this.apiMergedRowToBlock(row));
      } else {
        // 兼容旧草稿仅有 carbonCertList、无 carbonReportList 的情况
        this.certificateBlocks = certList.map((c) => this.apiMergedRowToBlock(c || {}));
      }
      this.$nextTick(() => {
        this.certificateBlocks.forEach((block, bi) => {
          this.ensureBlockUiState(block);
          this.clearBlockFormValidation(bi);
        });
      });
    },
    pickNonEmptyString(...candidates) {
      for (let i = 0; i < candidates.length; i += 1) {
        const c = candidates[i];
        if (c != null && String(c).trim() !== '') {
          return c;
        }
      }
      return '';
    },
    resolveReportNumberFromApi(apiRow) {
      if (apiRow.reportNumber != null && String(apiRow.reportNumber).trim() !== '') {
        return apiRow.reportNumber;
      }
      if (apiRow.reportDocumentNo != null && String(apiRow.reportDocumentNo).trim() !== '') {
        return apiRow.reportDocumentNo;
      }
      return '';
    },
    /**
     * 接口证书/报告编号回显：
     * - 新结构：certCode=证书编号，reportCode=报告编号（兼容旧字段 certificateNumber）
     * - 旧结构：reportCode 存证书编号，报告在 reportNumber / reportDocumentNo
     */
    applyApiRowToBlockCertificateReport(block, apiRow) {
      const legacyReport = this.resolveReportNumberFromApi(apiRow);
      let certField = '';
      if (apiRow.certCode != null && String(apiRow.certCode).trim() !== '') {
        certField = String(apiRow.certCode).trim();
      } else if (apiRow.certificateNumber != null && String(apiRow.certificateNumber).trim() !== '') {
        certField = String(apiRow.certificateNumber).trim();
      }
      const rc = apiRow.reportCode != null && String(apiRow.reportCode).trim() !== '' ? String(apiRow.reportCode).trim() : '';

      if (certField) {
        block.certificateNumber = certField;
        block.reportCode = rc || legacyReport;
        return;
      }
      if (legacyReport) {
        block.certificateNumber = rc;
        block.reportCode = legacyReport;
        return;
      }
      block.certificateNumber = '';
      block.reportCode = rc;
    },
    apiMergedRowToBlock(apiRow) {
      const block = createEmptyCertificateBlock();
      block.institutionName = apiRow.institutionName || '';
      block.publishDate = apiRow.publishDate || apiRow.validStart || '';
      block.systemBoundary = apiRow.systemBoundary !== undefined && apiRow.systemBoundary !== '' ? apiRow.systemBoundary : '';
      if (apiRow.timeBoundaryStart && apiRow.timeBoundaryEnd) {
        block.timeBoundary = [apiRow.timeBoundaryStart, apiRow.timeBoundaryEnd];
      }
      this.applyApiRowToBlockCertificateReport(block, apiRow);

      const certUrl = apiRow.certAttachmentUrl || apiRow.attachmentUrl;
      const certName = this.pickNonEmptyString(apiRow.certAttachmentName, apiRow.certAchmentName, apiRow.attachmentName);
      if (certUrl) {
        block.certificateAttachmentList = [{ name: certName, url: certUrl }];
      }
      const repUrl = apiRow.reportAttachmentUrl || apiRow.attachmentUrl;
      const repName = this.pickNonEmptyString(apiRow.reportAttachmentName, apiRow.reportAattachmentName, apiRow.attachmentName);
      if (repUrl) {
        block.reportAttachmentList = [{ name: repName, url: repUrl }];
      }

      /**
       * 详情/草稿兼容：
       * - 未来与提交一致：父级无产品扁平字段，仅 carbonReportChildrenList 有产品。
       * - 当前旧结构：父级带第一条产品，子列表为其余或全量；首条与父级重复时仅用子列表；否则父级+子列表并过滤与子级重复的项。
       */
      const children = Array.isArray(apiRow.carbonReportChildrenList) ? apiRow.carbonReportChildrenList : [];
      const hasParentProduct = this.hasProductShape(apiRow);
      const parentProd = productFieldsFromApiRow(apiRow);
      const childProds = children.map((c) => productFieldsFromApiRow(c));

      if (!hasParentProduct && childProds.length > 0) {
        block.products = childProds;
      } else if (hasParentProduct && childProds.length > 0) {
        if (this.isDuplicateFirstProduct(parentProd, childProds[0])) {
          block.products = childProds;
        } else {
          const extraChildren = childProds.filter((child) => !this.isDuplicateFirstProduct(parentProd, child));
          block.products = [parentProd, ...extraChildren];
        }
      } else if (hasParentProduct) {
        block.products = [parentProd];
      } else {
        block.products = [createEmptyProduct()];
      }
      this.ensureBlockUiState(block);
      return block;
    },
    /**
     * 新提交结构：carbonReportList 为按 siteId 分组的扁平行，无嵌套 carbonReportChildrenList。
     * 与旧结构区分：旧结构至少有一项带非空 carbonReportChildrenList，或行上无有效 siteId。
     */
    isFlatSiteGroupedReportList(rows) {
      if (!Array.isArray(rows) || rows.length === 0) {
        return false;
      }
      const hasNestedChildren = rows.some((r) => Array.isArray(r.carbonReportChildrenList) && r.carbonReportChildrenList.length > 0);
      if (hasNestedChildren) {
        return false;
      }
      return rows.every((r) => {
        const raw = r.siteId;
        const n = raw === null || raw === undefined || String(raw).trim() === '' ? NaN : Number(raw);
        return Number.isFinite(n) && n > 0;
      });
    },
    /** 将扁平 carbonReportList（同 siteId 多行=同证书块下多产品）还原为 certificateBlocks */
    flatCarbonReportListToBlocks(rows) {
      const bySiteId = new Map();
      rows.forEach((r) => {
        const n = Number(r.siteId);
        const key = Number.isFinite(n) && n > 0 ? n : 1;
        if (!bySiteId.has(key)) {
          bySiteId.set(key, []);
        }
        bySiteId.get(key).push(r);
      });
      const sortedSiteIds = Array.from(bySiteId.keys()).sort((a, b) => a - b);
      return sortedSiteIds.map((siteId) => this.apiFlatProductRowsToBlock(bySiteId.get(siteId)));
    },
    /** 同一 siteId 下的多行 API 数据 → 一个证书块（首行取证书/报告级字段，多行合并为 products） */
    apiFlatProductRowsToBlock(groupRows) {
      if (!Array.isArray(groupRows) || groupRows.length === 0) {
        return createEmptyCertificateBlock();
      }
      const first = groupRows[0];
      const block = createEmptyCertificateBlock();
      block.institutionName = first.institutionName || '';
      block.publishDate = first.publishDate || first.validStart || '';
      block.systemBoundary = first.systemBoundary !== undefined && first.systemBoundary !== '' ? first.systemBoundary : '';
      if (first.timeBoundaryStart && first.timeBoundaryEnd) {
        block.timeBoundary = [first.timeBoundaryStart, first.timeBoundaryEnd];
      }
      this.applyApiRowToBlockCertificateReport(block, first);
      const certUrl = first.certAttachmentUrl || first.attachmentUrl;
      const certName = this.pickNonEmptyString(first.certAttachmentName, first.certAchmentName, first.attachmentName);
      if (certUrl) {
        block.certificateAttachmentList = [{ name: certName, url: certUrl }];
      }
      const repUrl = first.reportAttachmentUrl || first.attachmentUrl;
      const repName = this.pickNonEmptyString(first.reportAttachmentName, first.reportAattachmentName, first.attachmentName);
      if (repUrl) {
        block.reportAttachmentList = [{ name: repName, url: repUrl }];
      }
      const products = groupRows.map((r) => productFieldsFromApiRow(r));
      block.products = products.length > 0 ? products : [createEmptyProduct()];
      this.ensureBlockUiState(block);
      return block;
    },
    /** 判断父级扁平首条与子列表首条是否为同一条产品（提交全量在子列表、与父级重复时的兼容） */
    isDuplicateFirstProduct(parentProd, firstChildProd) {
      if (!parentProd || !firstChildProd) {
        return false;
      }
      if (String(parentProd.productName || '').trim() !== String(firstChildProd.productName || '').trim()) {
        return false;
      }
      if (String(parentProd.carbonUnitValue || '').trim() !== String(firstChildProd.carbonUnitValue || '').trim()) {
        return false;
      }
      const pCat = parentProd.certificateMaterialCategory && parentProd.certificateMaterialCategory[0];
      const cCat = firstChildProd.certificateMaterialCategory && firstChildProd.certificateMaterialCategory[0];
      const pId = pCat && pCat.categoryId;
      const cId = cCat && cCat.categoryId;
      if (pId != null && cId != null && String(pId) !== String(cId)) {
        return false;
      }
      return true;
    },
    hasProductShape(apiRow) {
      return !!(
        (apiRow.productName && String(apiRow.productName).trim() !== '') ||
        (apiRow.certificateMaterialCategory && apiRow.certificateMaterialCategory.length) ||
        (apiRow.categoryName && String(apiRow.categoryName).trim() !== '') ||
        (apiRow.functionalUnit && String(apiRow.functionalUnit).trim() !== '') ||
        (apiRow.carbonUnit != null && String(apiRow.carbonUnit).trim() !== '') ||
        (apiRow.carbonUnitValue != null && String(apiRow.carbonUnitValue).trim() !== '')
      );
    },
    isCategoryFilled(value) {
      if (!value || !Array.isArray(value) || value.length === 0) {
        return false;
      }
      const first = value[0];
      const hasId = first.categoryId !== undefined && first.categoryId !== null && String(first.categoryId).trim() !== '';
      const hasName = first.categoryName && String(first.categoryName).trim() !== '';
      return hasId || hasName;
    },
    onCategoryChange(bi, pi, value) {
      this.$set(this.certificateBlocks[bi].products[pi], 'certificateMaterialCategory', value);
      this.$nextTick(() => {
        const formRef = this.$refs[`blockForm${bi}`];
        const form = Array.isArray(formRef) ? formRef[0] : formRef;
        if (!form) {
          return;
        }
        const prop = `products.${pi}.certificateMaterialCategory`;
        if (this.isCategoryFilled(value)) {
          form.clearValidate(prop);
        } else if (typeof form.validateField === 'function') {
          form.validateField(prop);
        }
      });
    },
    validateCarbonFootprintPairFields(bi, pi) {
      this.$nextTick(() => {
        const formRef = this.$refs[`blockForm${bi}`];
        const form = Array.isArray(formRef) ? formRef[0] : formRef;
        if (form && typeof form.validateField === 'function') {
          form.validateField(`products.${pi}.carbonUnitValue`);
          form.validateField(`products.${pi}.lifeCarbonList`, (errorMessage) => {
            this.syncLifeCarbonFieldError(bi, pi, errorMessage);
          });
        }
      });
    },
    /** 生命周期各阶段碳排放：各阶段碳排放量、占比均必填 */
    lifeCarbonListRules(bi, pi) {
      return [
        {
          required: true,
          validator: (rule, value, callback) => {
            const prod = this.certificateBlocks[bi] && this.certificateBlocks[bi].products[pi];
            if (!prod) {
              callback();
              return;
            }
            const unit = Number(prod.carbonUnit);
            const hasUnit = unit === 1 || unit === 2 || unit === 3;
            if (!hasUnit) {
              callback(new Error('请先选择每功能单位碳足迹单位'));
              return;
            }
            const list = prod.lifeCarbonList || [];
            for (let i = 0; i < list.length; i += 1) {
              const item = list[i];
              const name = LIFECYCLE_STAGE_NAMES[item.stageType] || '该阶段';
              const em = item.carbonEmission != null ? String(item.carbonEmission).trim() : '';
              if (!em) {
                callback(new Error(`请输入${name}碳排放量`));
                return;
              }
              const pr = item.proportion != null ? String(item.proportion).trim() : '';
              if (!pr) {
                callback(new Error(`请输入${name}占比`));
                return;
              }
            }
            callback();
          },
          trigger: ['blur', 'change'],
        },
      ];
    },
    validateLifeCarbonListFields(bi, pi) {
      this.$nextTick(() => {
        const formRef = this.$refs[`blockForm${bi}`];
        const form = Array.isArray(formRef) ? formRef[0] : formRef;
        if (form && typeof form.validateField === 'function') {
          form.validateField(`products.${pi}.lifeCarbonList`, (errorMessage) => {
            this.syncLifeCarbonFieldError(bi, pi, errorMessage);
          });
        }
      });
    },
    syncLifeCarbonFieldError(bi, pi, errorMessage) {
      this.$set(this.lifeCarbonFieldErrorKeys, `${bi}_${pi}`, !!errorMessage);
    },
    syncAllLifeCarbonFieldErrors() {
      this.certificateBlocks.forEach((block, bi) => {
        const products = block.products || [];
        products.forEach((_, pi) => {
          const form = this.getBlockForm(bi);
          const errorMessage = form
            ? this.getFormFieldError(form, `products.${pi}.lifeCarbonList`)
            : null;
          this.syncLifeCarbonFieldError(bi, pi, errorMessage);
        });
      });
    },
    /** 数值 + 单位（吨/千克/克）均必填，任一项未填则校验失败 */
    carbonFootprintPairRules(bi, pi) {
      return [
        {
          required: true,
          validator: (rule, value, callback) => {
            const prod = this.certificateBlocks[bi] && this.certificateBlocks[bi].products[pi];
            if (!prod) {
              callback();
              return;
            }
            const numStr = prod.carbonUnitValue != null ? String(prod.carbonUnitValue).trim() : '';
            const unit = Number(prod.carbonUnit);
            const hasValue = numStr !== '';
            const hasUnit = unit === 1 || unit === 2 || unit === 3;
            if (!hasValue && !hasUnit) {
              callback(new Error('请输入每功能单位碳足迹数值并选择单位'));
              return;
            }
            if (!hasValue) {
              callback(new Error('请输入每功能单位碳足迹数值'));
              return;
            }
            if (!hasUnit) {
              callback(new Error('请选择单位'));
              return;
            }
            callback();
          },
          trigger: ['blur', 'change'],
        },
      ];
    },
    onCarbonUnitValueInput(val, bi, pi) {
      this.sanitizeNumber(val, bi, pi, 'carbonUnitValue');
      this.validateCarbonFootprintPairFields(bi, pi);
    },
    sanitizeNumber(val, bi, pi, key) {
      const clean = val
        .replace(/[^\d.]/g, '')
        .replace(/^(\d*\.\d*?)\..*$/, '$1')
        .replace(/^0+(\d)/, '$1');
      this.certificateBlocks[bi].products[pi][key] = clean;
    },
    clearBlockFormValidation(bi) {
      const formRef = this.$refs[`blockForm${bi}`];
      const form = Array.isArray(formRef) ? formRef[0] : formRef;
      if (form && typeof form.clearValidate === 'function') {
        form.clearValidate();
      }
    },
    onLifecycleInput(data, bi, pi) {
      const prod = this.certificateBlocks[bi].products[pi];
      prod.lifeCarbonList = data.lifeCarbonList;
      this.validateLifeCarbonListFields(bi, pi);
    },
    onLifecycleCarbonUnitChange(val, bi, pi) {
      const prod = this.certificateBlocks[bi] && this.certificateBlocks[bi].products[pi];
      if (!prod) {
        return;
      }
      const normalized = normalizeLifecycleCarbonUnit(val);
      this.$set(prod, 'carbonUnit', normalized === '' ? '' : normalized);
      this.validateCarbonFootprintPairFields(bi, pi);
    },
    onCertUpload(res, bi) {
      if (res.success) {
        this.$set(this.certificateBlocks[bi], 'certificateAttachmentList', [
          {
            ...res.data,
            name: res.data.fileName,
            url: res.data.url,
          },
        ]);
        this.validateReportOrCertUploadFields(bi);
      }
    },
    onReportUpload(res, bi) {
      if (res.success) {
        this.$set(this.certificateBlocks[bi], 'reportAttachmentList', [
          {
            ...res.data,
            name: res.data.fileName,
            url: res.data.url,
          },
        ]);
        this.validateReportOrCertUploadFields(bi);
      }
    },
    /** 详情只读：点击附件名称下载 */
    downloadAttachment(file) {
      const url = file && (file.url || file.fileUrl);
      if (!url) {
        this.$message.warning('附件地址不存在');
        return;
      }
      const name = (file.name || file.fileName || '附件.pdf').trim();
      const downloadByBlob = async () => {
        const response = await fetch(url);
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = name;
        a.click();
        a.remove();
        window.URL.revokeObjectURL(objectUrl);
      };
      downloadByBlob().catch(() => {
        window.open(url, '_blank');
      });
    },
    confirmStructureDelete() {
      return this.$confirm(DELETE_CONFIRM_MESSAGE, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => true)
        .catch(() => false);
    },
    doRemoveProduct(bi, pi) {
      const block = this.certificateBlocks[bi];
      if (!block || block.products.length <= 1) {
        return;
      }
      const next = block.products.filter((_, idx) => idx !== pi);
      let nextActive = block.activeProductIndex != null ? block.activeProductIndex : 0;
      if (pi < nextActive) {
        nextActive -= 1;
      } else if (pi === nextActive) {
        nextActive = Math.min(nextActive, next.length - 1);
      }
      this.$set(block, 'products', next);
      this.$set(block, 'activeProductIndex', nextActive);
      this.$nextTick(() => {
        this.clearBlockFormValidation(bi);
      });
    },
    async removeProduct(bi, pi) {
      if (this.structureLocked) {
        return;
      }
      const block = this.certificateBlocks[bi];
      if (!block || block.products.length <= 1) {
        return;
      }
      const confirmed = await this.confirmStructureDelete();
      if (!confirmed) {
        return;
      }
      this.doRemoveProduct(bi, pi);
    },
    addProduct(bi) {
      if (this.structureLocked) {
        return;
      }
      const block = this.certificateBlocks[bi];
      if (!block || block.products.length >= this.maxProductsPerCertificate) {
        this.$message.warning(`单份证书下最多可录入${this.maxProductsPerCertificate}个产品`);
        return;
      }
      const newIndex = block.products.length;
      this.$set(block, 'products', block.products.concat([createEmptyProduct()]));
      this.$set(block, 'activeProductIndex', newIndex);
      this.$nextTick(() => {
        this.clearBlockFormValidation(bi);
      });
    },
    async removeCertificateBlock(bi) {
      if (this.structureLocked) {
        return;
      }
      if (this.certificateBlocks.length <= 1) {
        return;
      }
      const confirmed = await this.confirmStructureDelete();
      if (!confirmed) {
        return;
      }
      this.certificateBlocks.splice(bi, 1);
    },
    addCertificateBlock() {
      if (this.structureLocked) {
        return;
      }
      if (this.certificateBlocks.length >= this.maxCertificateBlocks) {
        this.$message.warning(`最多可新增${this.maxCertificateBlocks}条产品碳足迹信息`);
        return;
      }
      const newBlock = createEmptyCertificateBlock();
      this.certificateBlocks.push(newBlock);
      const last = this.certificateBlocks.length - 1;
      this.$set(this.certificateBlocks[last], 'collapsed', false);
      this.$nextTick(() => {
        this.clearBlockFormValidation(last);
        this.scrollToBlock(last);
      });
    },
    scrollToBlock(bi) {
      const ref = this.$refs[`pcfBlock${bi}`];
      const el = Array.isArray(ref) ? ref[0] : ref;
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    getCertReportFiles(block) {
      const certFile = block.certificateAttachmentList && block.certificateAttachmentList[0];
      const reportFile = block.reportAttachmentList && block.reportAttachmentList[0];
      return { certFile, reportFile };
    },
    hasUploadedAttachment(file) {
      return !!(file && String(file.url || '').trim());
    },
    validateReportOrCertUploadFields(bi) {
      const form = this.getBlockForm(bi);
      if (!form || typeof form.validateField !== 'function') {
        return;
      }
      const props = ['reportAttachmentList', 'certificateAttachmentList'];
      props.forEach((prop) => {
        form.validateField(prop);
      });
    },
    validateReportOrCertUploadBlock(bi) {
      const block = this.certificateBlocks[bi];
      const form = this.getBlockForm(bi);
      if (!block || !form) {
        return true;
      }
      const { certFile, reportFile } = this.getCertReportFiles(block);
      if (this.hasUploadedAttachment(certFile) || this.hasUploadedAttachment(reportFile)) {
        if (typeof form.clearValidate === 'function') {
          form.clearValidate(['reportAttachmentList', 'certificateAttachmentList']);
        }
        return true;
      }
      this.focusValidationTarget(bi, 0);
      this.validateReportOrCertUploadFields(bi);
      return false;
    },
    validateReportOrCertUpload() {
      for (let bi = 0; bi < this.certificateBlocks.length; bi += 1) {
        if (!this.validateReportOrCertUploadBlock(bi)) {
          return false;
        }
      }
      return true;
    },
    /** 单条产品 + 证书块上下文 → 接口行（含与证书一致的附件与编号） */
    buildApiSlice(block, product) {
      const category = product.certificateMaterialCategory && product.certificateMaterialCategory[0];
      const timeStart = block.timeBoundary && block.timeBoundary[0];
      const timeEnd = block.timeBoundary && block.timeBoundary[1];
      const validStart = block.publishDate || timeStart;
      const validEnd = timeEnd || block.publishDate || validStart;
      const { certFile, reportFile } = this.getCertReportFiles(block);
      const footprintValue = product.carbonUnitValue != null ? String(product.carbonUnitValue).trim() : '';
      const footprintUnitNum = Number(product.carbonUnit);
      const carbonUnitNormalized = footprintUnitNum === 1 || footprintUnitNum === 2 || footprintUnitNum === 3 ? footprintUnitNum : '';

      const slice = {
        institutionName: block.institutionName,
        productName: product.productName,
        specifications: product.specifications,
        systemBoundary: block.systemBoundary,
        timeBoundaryStart: timeStart,
        timeBoundaryEnd: timeEnd,
        functionalUnit: product.functionalUnit,
        carbonUnitValue: footprintValue,
        carbonUnit: carbonUnitNormalized,
        lifecycleCarbonUnit: carbonUnitNormalized,
        certificateMaterialCategory: product.certificateMaterialCategory ? JSON.parse(JSON.stringify(product.certificateMaterialCategory)) : [],
        categoryName: category && category.categoryName,
        categoryId: category && category.categoryId,
        firstCategoryId: category && category.firstCategoryId,
        secondCategoryId: category && category.secondCategoryId,
        thirdCategoryId: category && category.thirdCategoryId,
        fourthCategoryId: category && category.fourthCategoryId,
        lifeCarbonList: product.lifeCarbonList ? JSON.parse(JSON.stringify(product.lifeCarbonList)) : defaultLifeCarbonList(),
        reportAttachmentUrl: reportFile && reportFile.url,
        reportAttachmentName: reportFile && reportFile.name,
        publishDate: block.publishDate,
        validStart,
        validEnd,
        certAttachmentUrl: certFile && certFile.url,
        certAttachmentName: certFile && certFile.name,
        standardName: product.standardName || '',
      };
      slice.reportCode = block.reportCode != null && String(block.reportCode).trim() !== '' ? String(block.reportCode).trim() : '';
      if (block.certificateNumber != null && String(block.certificateNumber).trim() !== '') {
        slice.certCode = String(block.certificateNumber).trim();
      }
      if (
        category &&
        isMaterialCategoryLeafNode(category) &&
        category.mdmCategoryCode != null &&
        String(category.mdmCategoryCode).trim() !== ''
      ) {
        slice.mdmCategoryCode = String(category.mdmCategoryCode).trim();
      }
      return slice;
    },
    /**
     * 提交/保存：carbonReportList 为扁平列表，每项为原 carbonReportChildrenList 的一条（含证书块上下文字段）+ siteId（产品碳足迹1→1，2→2…）
     */
    buildFlatCarbonReportList() {
      const carbonReportList = [];
      this.certificateBlocks.forEach((block, blockIndex) => {
        const siteId = blockIndex + 1;
        const rawList = Array.isArray(block.products) && block.products.length ? block.products : [createEmptyProduct()];
        rawList.forEach((product) => {
          const row = this.buildApiSlice(block, product);
          row.siteId = siteId;
          carbonReportList.push(row);
        });
      });
      return carbonReportList;
    },
    getPayload() {
      return {
        carbonReportList: this.buildFlatCarbonReportList(),
      };
    },
    getBlockForm(bi) {
      const formRef = this.$refs[`blockForm${bi}`];
      if (!formRef) {
        return null;
      }
      return Array.isArray(formRef) ? formRef[0] : formRef;
    },
    productDisplayLabel(block, pi) {
      const prod = block.products && block.products[pi];
      const name = prod && prod.productName != null ? String(prod.productName).trim() : '';
      return name ? `「${name}」` : `「产品${pi + 1}」`;
    },
    formatValidationTip(bi, pi, rawMessage, isBlockField = false) {
      const footprintLabel = `「产品碳足迹${bi + 1}」`;
      if (isBlockField) {
        return `${footprintLabel}：${rawMessage}`;
      }
      const block = this.certificateBlocks[bi];
      const productLabel = this.productDisplayLabel(block, pi);
      return `${footprintLabel}下的${productLabel}：${rawMessage}`;
    },
    getProductValidateProps(pi) {
      return [
        `products.${pi}.certificateMaterialCategory`,
        `products.${pi}.productName`,
        `products.${pi}.functionalUnit`,
        `products.${pi}.carbonUnitValue`,
        `products.${pi}.lifeCarbonList`,
      ];
    },
    getBlockValidateProps() {
      return [
        'institutionName',
        'publishDate',
        'systemBoundary',
        'reportAttachmentList',
        'certificateAttachmentList',
      ];
    },
    isUploadValidationProp(prop) {
      return prop === 'reportAttachmentList' || prop === 'certificateAttachmentList';
    },
    validateFormField(form, prop) {
      return new Promise((resolve) => {
        if (!form || typeof form.validateField !== 'function') {
          resolve(null);
          return;
        }
        form.validateField(prop, (errorMessage) => {
          resolve(errorMessage || null);
        });
      });
    },
    focusValidationTarget(bi, pi) {
      const block = this.certificateBlocks[bi];
      if (!block) {
        return;
      }
      if (block.collapsed) {
        this.$set(block, 'collapsed', false);
      }
      if (pi != null && pi >= 0 && block.products && block.products[pi]) {
        this.$set(block, 'activeProductIndex', pi);
      }
    },
    getFormFieldError(form, prop) {
      if (!form || !form.fields || !form.fields.length) {
        return null;
      }
      const field = form.fields.find((f) => f.prop === prop);
      if (field && field.validateState === 'error') {
        return field.validateMessage || '请完善必填项';
      }
      return null;
    },
    /** 在 form.validate 已标红的前提下，按 产品碳足迹→产品 顺序取第一个错误（用于弹窗） */
    findFirstValidationErrorFromFields() {
      for (let bi = 0; bi < this.certificateBlocks.length; bi += 1) {
        const block = this.certificateBlocks[bi];
        const form = this.getBlockForm(bi);
        if (!form) {
          continue;
        }
        const productCount = block.products ? block.products.length : 0;
        for (let pi = 0; pi < productCount; pi += 1) {
          const props = this.getProductValidateProps(pi);
          for (let i = 0; i < props.length; i += 1) {
            const errorMessage = this.getFormFieldError(form, props[i]);
            if (errorMessage) {
              return {
                bi,
                pi,
                message: this.formatValidationTip(bi, pi, errorMessage, false),
              };
            }
          }
        }
        const blockProps = this.getBlockValidateProps();
        for (let i = 0; i < blockProps.length; i += 1) {
          const errorMessage = this.getFormFieldError(form, blockProps[i]);
          if (errorMessage) {
            return {
              bi,
              pi: block.activeProductIndex != null ? block.activeProductIndex : 0,
              prop: blockProps[i],
              message: this.formatValidationTip(bi, null, errorMessage, true),
            };
          }
        }
      }
      return null;
    },
    /** 兜底：按顺序 validateField 扫描（不改变原有 validate 逻辑） */
    async findFirstValidationErrorByScan() {
      for (let bi = 0; bi < this.certificateBlocks.length; bi += 1) {
        const block = this.certificateBlocks[bi];
        const form = this.getBlockForm(bi);
        if (!form) {
          continue;
        }
        const productCount = block.products ? block.products.length : 0;
        for (let pi = 0; pi < productCount; pi += 1) {
          const props = this.getProductValidateProps(pi);
          for (let i = 0; i < props.length; i += 1) {
            const errorMessage = await this.validateFormField(form, props[i]);
            if (errorMessage) {
              return {
                bi,
                pi,
                message: this.formatValidationTip(bi, pi, errorMessage, false),
              };
            }
          }
        }
        const blockProps = this.getBlockValidateProps();
        for (let i = 0; i < blockProps.length; i += 1) {
          const errorMessage = await this.validateFormField(form, blockProps[i]);
          if (errorMessage) {
            return {
              bi,
              pi: block.activeProductIndex != null ? block.activeProductIndex : 0,
              prop: blockProps[i],
              message: this.formatValidationTip(bi, null, errorMessage, true),
            };
          }
        }
      }
      return null;
    },
    async findFirstValidationError() {
      await this.$nextTick();
      const fromFields = this.findFirstValidationErrorFromFields();
      if (fromFields) {
        return fromFields;
      }
      return this.findFirstValidationErrorByScan();
    },
    /** 提交校验失败时仅弹出首个错误提示，不替代 form.validate 的表单项校验 */
    async showSubmitValidationTip() {
      if (this._submitValidationTipShown) {
        return;
      }
      this._submitValidationTipShown = true;
      const firstError = await this.findFirstValidationError();
      if (firstError && this.isUploadValidationProp(firstError.prop)) {
        this.focusValidationTarget(firstError.bi, firstError.pi);
        return;
      }
      if (firstError && firstError.message) {
        this.$message.warning(firstError.message);
      } else {
        this.$message.warning('请完善产品碳足迹必填信息后再提交');
      }
    },
    submit() {
      this._submitValidationTipShown = false;
      return new Promise((resolve, reject) => {
        Promise.all(
          this.certificateBlocks.map((_, bi) => {
            const form = this.getBlockForm(bi);
            if (!form || typeof form.validate !== 'function') {
              return Promise.resolve();
            }
            return form.validate();
          })
        )
          .then(() => {
            resolve({
              success: true,
              data: this.getPayload(),
            });
          })
          .catch(async () => {
            await this.$nextTick();
            this.syncAllLifeCarbonFieldErrors();
            await this.showSubmitValidationTip();
            reject(new Error('validation'));
          });
      });
    },
    /**
     * 保存草稿：按「产品碳足迹1→产品1→产品2…→产品碳足迹2…」顺序校验
     * 每个产品必填：认证物资类别、产品名称（与提交全量校验区分；碳足迹报告/证书仅提交时校验）
     */
    validateSaveDraftManual() {
      for (let bi = 0; bi < this.certificateBlocks.length; bi += 1) {
        const block = this.certificateBlocks[bi];
        const products = block && block.products ? block.products : [];
        for (let pi = 0; pi < products.length; pi += 1) {
          const prod = products[pi];
          if (!this.isCategoryFilled(prod && prod.certificateMaterialCategory)) {
            this.focusValidationTarget(bi, pi);
            this.$message.warning(
              this.formatValidationTip(bi, pi, '请选择认证物资类别', false)
            );
            return false;
          }
          const productName = prod && prod.productName != null ? String(prod.productName).trim() : '';
          if (!productName) {
            this.focusValidationTarget(bi, pi);
            this.$message.warning(
              this.formatValidationTip(bi, pi, '请输入产品名称', false)
            );
            return false;
          }
        }
      }
      return true;
    },
    save() {
      return new Promise((resolve, reject) => {
        if (!this.validateSaveDraftManual()) {
          reject(new Error('validation'));
          return;
        }
        resolve(this.getPayload());
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.product-carbon-footprint {
  .report-cert-upload-hint {
    margin: 0 0 12px;
    padding-left: 150px;
    font-size: 12px;
    line-height: 1.5;
    color: #f56c6c;
  }

  .report-cert-upload-form-item {
    ::v-deep .el-form-item__content {
      line-height: normal;
    }

    ::v-deep .el-form-item__error {
      position: static;
      padding-top: 4px;
      font-size: 12px;
      line-height: 1.5;
    }
  }

  .pcf-list-wrap {
    background: #fff;
  }

  .pcf-block-card {
    /* margin-bottom: 20px; */
    &:last-child {
      margin-bottom: 0;
    }
    & + .pcf-block-card {
      /* padding-top: 16px; */
      border-top: 1px solid rgba(217, 217, 217, 1);
    }
    &.is-collapsed-row {
      .pcf-block-header {
        border-bottom-color: transparent;
      }
    }
  }

  .pcf-block-body {
    display: grid;
    grid-template-rows: 1fr;
    transition: grid-template-rows 0.28s ease;
    &.is-collapsed {
      grid-template-rows: 0fr;
    }
  }
  .pcf-block-body-inner {
    min-height: 0;
    overflow: hidden;
  }

  .pcf-block-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 44px;
    padding: 8px 0 8px;
    margin: 0;
    border-bottom: 1px solid rgba(217, 217, 217, 1);
    background: transparent;
  }
  .pcf-block-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    cursor: pointer;
    user-select: none;
  }
  .pcf-block-title-tag {
    display: inline-block;
    padding: 4px 10px;
    font-size: 14px;
    font-weight: 500;
    font-family: SourceHanSansCN-Medium;
    color: #333;
    line-height: 20px;
    background: linear-gradient(90deg, rgba(102, 171, 254, 0.35) 0%, rgba(73, 151, 246, 0.08) 100%);
  }
  .pcf-collapse-box {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: 1px solid #2587ff;
    background: #fff;
    color: #2587ff;
    font-size: 12px;
    line-height: 1;
    box-sizing: border-box;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
  }
  .pcf-block-header-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    &.is-actions-hidden {
      visibility: hidden;
      pointer-events: none;
    }
  }
  .pcf-add-certificate-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    padding: 0 14px;
    border: 1px solid #2587ff;
    border-radius: 2px;
    background: #fff;
    color: #2587ff;
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s, color 0.15s;
    &:hover {
      background: rgba(37, 135, 255, 0.06);
    }
  }
  .pcf-block-form {
    padding: 16px 0 0;
  }
  .product-tabs-panel {
    margin: 4px 0 16px;
    padding: 12px;
    background: #f5f5f5;
    border: 1px solid #b3d4ff;
  }
  .product-tabs-inner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }
  .product-tab-add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    height: 32px;
    padding: 0 14px;
    border: 1px solid #2587ff;
    border-radius: 2px;
    background: #2587ff;
    color: #fff;
    font-size: 14px;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
    &:hover:not(:disabled) {
      background: #1a6fe0;
      border-color: #1a6fe0;
    }
    &:disabled {
      background: #f0f0f0;
      border-color: #dcdfe6;
      color: #c0c4cc;
      cursor: not-allowed;
      opacity: 1;
    }
  }
  .product-tab-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    max-width: 160px;
    height: 32px;
    padding: 0 10px;
    border: 1px solid #2587ff;
    border-radius: 2px;
    background: #fff;
    color: #2587ff;
    font-size: 13px;
    line-height: 32px;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
    &.active {
      background: #e8f3ff;
      border-color: #2587ff;
      box-shadow: 0 0 0 1px #2587ff inset;
    }
  }
  .product-tab-text {
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 120px;
    vertical-align: middle;
  }
  .product-tab-close {
    font-size: 12px;
    color: #999;
    flex-shrink: 0;
    line-height: 1;
    &:hover {
      color: #e50113;
    }
  }
  .product-tabs-limit {
    display: block;
    margin: -8px 0 12px;
    text-align: left;
  }
  .product-panel {
    margin-bottom: 8px;
  }
  .limit-tip {
    font-size: 12px;
    color: #999;
  }
  .readonly-file-name {
    line-height: 32px;
    color: #606266;
    font-size: 14px;
    &.is-link {
      color: #2587ff;
      cursor: pointer;
      &:hover {
        text-decoration: underline;
      }
    }
  }
.pcf-footer-add {
    margin-top: 12px;
  }
  .pcf-footer-add-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 40px;
    padding: 0 16px;
    border: 1px solid #2587ff;
    border-radius: 2px;
    background: rgba(37, 135, 255, 0.08);
    color: #2587ff;
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s;
    &:hover {
      background: rgba(37, 135, 255, 0.14);
    }
  }
}
::v-deep.el-form-item {
  .el-form-item__label {
    padding: 0px;
    padding-right: 10px;
    color: #666;
  }
  .el-form-item__label:after {
    content: '';
    display: none;
  }
}
::v-deep.el-form-item.cursor-form-item {
  .el-form-item__label {
    line-height: 1;
  }
}
::v-deep.el-form-item.is-required:not(.is-no-asterisk) .el-form-item__label-wrap > .el-form-item__label:before,
::v-deep.el-form-item.is-required:not(.is-no-asterisk) > .el-form-item__label:before {
  content: '*';
  color: rgba(240, 37, 15, 1);
  margin-right: 0px;
}
::v-deep.el-form .el-input__inner,
::v-deep.el-form .el-input,
::v-deep.el-form .el-date-editor,
::v-deep.el-form .el-select .el-input__inner,
::v-deep.el-input--small .el-input__inner {
  height: 30px !important;
  line-height: 30px !important;
}
::v-deep.el-radio {
  margin-right: 0px;
}
::v-deep.el-radio__label {
  color: #333;
}
::v-deep.el-radio__input.is-checked + .el-radio__label {
  color: #333;
}

.lifecycle-block-form-item {
  margin-bottom: 18px;
}
::v-deep .lifecycle-block-form-item .el-form-item__content {
  margin-left: 0 !important;
  line-height: normal;
}
::v-deep .lifecycle-block-form-item .el-form-item__error {
  position: static;
  padding-top: 4px;
  padding-left: 160px;
  font-size: 12px;
  line-height: 1.5;
}
::v-deep .lifecycle-block-form-item .lifecycle-emissions {
  width: 100%;
}

::v-deep .label-wrap-form-item.el-form-item {
  display: flex;
  align-items: flex-start;
}
::v-deep .label-wrap-form-item .el-form-item__content {
  margin-left: 0 !important;
}
::v-deep .label-wrap-form-item .el-form-item__label {
  float: none;
  width: 160px !important;
  min-width: 160px;
  max-width: 160px;
  box-sizing: border-box;
  white-space: normal;
  line-height: 1.4;
  padding-top: 6px;
  padding-right: 10px;
  padding-left: 0;
  text-align: right;
  word-break: break-all;
  overflow-wrap: anywhere;
}

.carbon-footprint-value-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
  width: 100%;
}

.carbon-value-input {
  width: 266px;
  max-width: 100%;
}

.carbon-unit-select {
  width: 280px;
  max-width: 100%;
}

::v-deep .carbon-unit-select .el-input__inner {
  height: 30px !important;
  line-height: 30px !important;
}

::v-deep .material-category-form-item.is-error .container-purchase .left .el-input__inner {
  border-color: #dcdfe6 !important;
}
::v-deep .material-category-form-item.is-error .container-purchase .left .el-input.is-disabled .el-input__inner {
  border-color: #e4e7ed !important;
}
::v-deep .material-category-form-item.is-error .container-purchase .right {
  border-color: #f56c6c !important;
}
</style>
